import React, { useEffect, useRef, useState, useDeferredValue, useCallback } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import RoundLoader from '../../../../components/Loader/RoundLoader';
import styles from './GooglePlacesAutocomplete.module.scss';

const libraries = ['places'];

const GooglePlacesAutocomplete = React.memo(({ value, visible, onSelect, onClose }) => {
    const [predictions, setPredictions] = useState([]);
    const [loading, setLoading] = useState(false);

    const autocompleteService = useRef(null);
    const suggestionRef = useRef(null);
    const lastFetchedValue = useRef('');
    const requestId = useRef(0);
    const debounceTimerRef = useRef(null);
    const onCloseRef = useRef(onClose);
    const deferredValue = useDeferredValue(value);

    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries,
    });

    useEffect(() => {
        if (isLoaded && !autocompleteService.current) {
            autocompleteService.current = new window.google.maps.places.AutocompleteService();
        }
    }, [isLoaded]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (suggestionRef.current && !suggestionRef.current.contains(e.target)) {
                onCloseRef.current();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const trimmed = deferredValue?.trim() || '';

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }

        if (!isLoaded || !autocompleteService.current || trimmed.length < 2) {
            setPredictions([]);
            setLoading(false);
            return;
        }

        if (trimmed === lastFetchedValue.current) return;

        const loaderTimerId = setTimeout(() => setLoading(true), 200);

        debounceTimerRef.current = setTimeout(() => {
            const currentRequestId = ++requestId.current;
            lastFetchedValue.current = trimmed;

            autocompleteService.current.getPlacePredictions(
                { input: trimmed, types: ['geocode'], componentRestrictions: { country: 'gb' } },
                (results, status) => {
                    clearTimeout(loaderTimerId);

                    if (currentRequestId !== requestId.current) return;

                    setLoading(false);

                    if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
                        setPredictions(results);
                    } else {
                        setPredictions([]);
                    }
                }
            );
        }, 400);

        return () => {
            clearTimeout(debounceTimerRef.current);
            clearTimeout(loaderTimerId);
        };
    }, [deferredValue, isLoaded]);

    const handleSelect = useCallback((prediction) => {
        onSelect({
            name: prediction.description,
            placeId: prediction.place_id,
        });
    }, [onSelect]);

    if (loading && visible) {
        return <RoundLoader className={styles.whiteLoader} />;
    }

    if (!visible || predictions.length === 0) return null;

    return (
        <ul className={styles.suggestionBox} ref={suggestionRef}>
            {predictions.map((prediction) => (
                <li
                    key={prediction.place_id}
                    className={styles.suggestionItem}
                    onClick={() => handleSelect(prediction)}
                >
                    {prediction.description}
                </li>
            ))}
        </ul>
    );
});

export default GooglePlacesAutocomplete;